import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, RefreshCw, Link as LinkIcon, AlertCircle, X, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function GCPIntegrationPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  const [projectId, setProjectId] = useState('');
  const [serviceAccountEmail, setServiceAccountEmail] = useState(null);
  const [securityData, setSecurityData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  
  const [credentials, setCredentials] = useState({
    serviceAccountJson: '',
    projectId: ''
  });

  useEffect(() => {
    checkGCPStatus();
  }, [viewingOrg]);

  const checkGCPStatus = async () => {
    try {
      // Add view_tenant_id param if super admin is viewing a tenant
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/gcp/status${params}`);
      setConnected(response.data.connected);
      setConnectedAt(response.data.connected_at);
      setProjectId(response.data.project_id || '');
      setServiceAccountEmail(response.data.service_account_email);
      
      if (response.data.connected) {
        fetchSecurityFindings();
      } else {
        setSecurityData(null);
      }
    } catch (error) {
      console.error('Failed to check GCP status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSecurityFindings = async () => {
    try {
      // Add view_tenant_id param if super admin is viewing a tenant
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/gcp/security-findings${params}`);
      setSecurityData(response.data);
    } catch (error) {
      console.error('Failed to fetch Security Command Center data:', error);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/gcp/connect`, null, {
        params: {
          service_account_json: credentials.serviceAccountJson,
          project_id: credentials.projectId
        }
      });
      
      toast.success('GCP connected successfully!');
      setShowConnectModal(false);
      checkGCPStatus();
      fetchSecurityFindings();
    } catch (error) {
      toast.error('Failed to connect GCP');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/gcp/sync-controls`);
      toast.success(`Successfully synced! Updated ${response.data.controls_updated} controls.`);
      fetchSecurityFindings();
    } catch (error) {
      toast.error('Failed to sync controls');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect GCP?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/gcp/disconnect`);
      toast.success('GCP disconnected');
      setConnected(false);
      setSecurityData(null);
    } catch (error) {
      toast.error('Failed to disconnect GCP');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gcp-integration-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
            GCP Security Command Center Integration
          </h1>
          <span className="px-3 py-1 bg-slate-100 text-slate-800 text-sm font-medium rounded-sm">
            Demo Mode
          </span>
        </div>
        <p className="text-slate-600 mt-2">
          Connect your GCP account to automatically assess ISO 27001 and SOC 2 controls based on Security Hub findings
        </p>
      </div>

      {/* Connection Status */}
      <div className="border border-border rounded-sm bg-white p-6">
        {/* Connected Account Info Banner */}
        {connected && (projectId || serviceAccountEmail) && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#4285F4] rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-xs">GCP</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-slate-900">Connected to GCP Security Command Center</span>
                </div>
                <p className="text-sm text-slate-600">
                  {projectId && <span className="font-medium">Project: {projectId}</span>}
                  {projectId && serviceAccountEmail && <span> · </span>}
                  {serviceAccountEmail && <span>{serviceAccountEmail}</span>}
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {connected ? (
              <CheckCircle className="w-6 h-6 text-green-600" />
            ) : (
              <XCircle className="w-6 h-6 text-slate-600" />
            )}
            <div>
              <h2 className="text-lg font-heading font-semibold">Connection Status</h2>
              <p className="text-sm text-slate-600">
                {connected
                  ? `Connected to project ${projectId} on ${new Date(connectedAt).toLocaleString()}`
                  : 'Not connected'}
              </p>
            </div>
          </div>
          
          {!connected ? (
            <Button
              onClick={() => setShowConnectModal(true)}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Connect GCP
            </Button>
          ) : (
            <Button
              onClick={handleDisconnect}
              variant="outline"
              className="rounded-sm text-red-600 hover:bg-slate-50"
            >
              Disconnect
            </Button>
          )}
        </div>

        {!connected && (
          <div className="mt-4 p-4 bg-slate-50 border border-slate-700 rounded-sm">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-900">
                <p className="font-medium mb-2">🎭 Demo Mode Active:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Click "Connect GCP" to create a demo connection with mock Security Hub data</li>
                  <li>No real GCP credentials required - test the integration immediately</li>
                  <li>Mock data: 82.3% compliance score, 143 findings (28 active, 115 resolved)</li>
                  <li>Sample findings: CIS GCP Foundation Benchmark, PCI-DSS standards</li>
                  <li>To use real GCP data, configure GCP_DEMO_MODE=false and provide service account credentials</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Hub Data Display */}
      {connected && securityData && (
        <div className="border border-border rounded-sm bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold">GCP Security Command Center Compliance</h2>
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Controls'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm font-medium text-slate-600 mb-1">Compliance Score</p>
              <p className="text-3xl font-heading font-semibold text-green-600">
                {securityData.compliance_score}%
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm font-medium text-slate-600 mb-1">Total Checks</p>
              <p className="text-3xl font-heading font-semibold text-slate-900">
                {securityData.total_findings}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm font-medium text-slate-600 mb-1">Passed</p>
              <p className="text-3xl font-heading font-semibold text-green-600">
                {securityData.resolved_findings}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm font-medium text-slate-600 mb-1">Failed</p>
              <p className="text-3xl font-heading font-semibold text-red-600">
                {securityData.active_findings}
              </p>
            </div>
          </div>

          {/* Findings Severity */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Findings by Severity</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-slate-50 rounded-sm">
                <p className="text-sm text-slate-600">Critical</p>
                <p className="text-2xl font-heading font-semibold text-red-600">
                  {securityData.critical_findings}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-sm">
                <p className="text-sm text-slate-600">High</p>
                <p className="text-2xl font-heading font-semibold text-slate-600">
                  {securityData.high_findings}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-sm">
                <p className="text-sm text-slate-600">Medium</p>
                <p className="text-2xl font-heading font-semibold text-slate-600">
                  {securityData.medium_findings}
                </p>
              </div>
            </div>
          </div>

          {/* Security Standards */}
          {securityData.standards && securityData.standards.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Security Standards</h3>
              <div className="space-y-2">
                {securityData.standards.map((standard, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-slate-600" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{standard.name}</p>
                        <p className="text-xs text-slate-600">Version {standard.version}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-green-600">
                        {standard.compliance_percentage}%
                      </p>
                      <p className="text-xs text-slate-600">Compliance</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sample Findings */}
          {securityData.findings && securityData.findings.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Sample Findings</h3>
              <div className="space-y-2">
                {securityData.findings.slice(0, 5).map((finding, index) => (
                  <div key={index} className="p-3 border border-border rounded-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-slate-600">{finding.id}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm ${
                            finding.compliance_status === 'PASSED' 
                              ? 'bg-slate-100 text-green-800' 
                              : 'bg-slate-100 text-red-800'
                          }`}>
                            {finding.compliance_status}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm ${
                            finding.severity === 'CRITICAL' 
                              ? 'bg-slate-100 text-red-800' 
                              : finding.severity === 'HIGH'
                              ? 'bg-slate-100 text-slate-800'
                              : 'bg-slate-100 text-slate-800'
                          }`}>
                            {finding.severity}
                          </span>
                        </div>
                        <p className="text-sm text-slate-900">{finding.title}</p>
                        <p className="text-xs text-slate-600 mt-1">{finding.resource_type}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="border border-border rounded-sm bg-white p-6">
        <h2 className="text-lg font-heading font-semibold mb-4">How It Works</h2>
        <div className="space-y-3 text-sm text-slate-700">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">
              1
            </div>
            <p>Connect your GCP account using IAM access keys (in demo mode, no credentials needed)</p>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">
              2
            </div>
            <p>We fetch compliance data from GCP Security Command Center (CIS GCP Foundations, GCP Best Practices)</p>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">
              3
            </div>
            <p>Security Hub findings are mapped to ISO 27001 and SOC 2 controls</p>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">
              4
            </div>
            <p>Your controls are automatically updated based on GCP security posture</p>
          </div>
        </div>
      </div>

      {/* Connect Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-md w-full">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold">Connect GCP Account</h2>
              <button onClick={() => setShowConnectModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleConnect} className="p-6 space-y-4">
              <div className="p-3 bg-slate-50 border border-slate-700 rounded-sm text-sm text-slate-900">
                <p className="font-medium mb-1">🎭 Demo Mode</p>
                <p>Click Connect to create demo connection. No real credentials needed!</p>
              </div>

              <div>
                <Label htmlFor="projectId">GCP Project ID</Label>
                <Input
                  id="projectId"
                  value={credentials.projectId}
                  onChange={(e) => setCredentials({ ...credentials, projectId: e.target.value })}
                  placeholder="my-project-123456"
                  disabled
                />
              </div>

              <div>
                <Label htmlFor="serviceAccountJson">Service Account JSON</Label>
                <textarea
                  id="serviceAccountJson"
                  value={credentials.serviceAccountJson}
                  onChange={(e) => setCredentials({ ...credentials, serviceAccountJson: e.target.value })}
                  placeholder='{"type": "service_account", "project_id": "..."}'
                  className="w-full rounded-sm border border-input px-3 py-2 text-sm h-24 font-mono text-xs"
                  disabled
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm flex-1">
                  Connect (Demo)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowConnectModal(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
